import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

type AuthorizationDetails = {
  client?: { name?: string; client_name?: string; redirect_uri?: string } | null;
  scope?: string;
  redirect_url?: string;
  redirect_to?: string;
};

type OAuthResult = { data: AuthorizationDetails | null; error: { message: string } | null };

/** Minimal typed wrapper for the beta `supabase.auth.oauth` namespace. */
const oauth = (supabase.auth as unknown as {
  oauth: {
    getAuthorizationDetails: (id: string) => Promise<OAuthResult>;
    approveAuthorization: (id: string) => Promise<OAuthResult>;
    denyAuthorization: (id: string) => Promise<OAuthResult>;
  };
}).oauth;

const SCOPE_LABELS: Record<string, string> = {
  openid: 'Confirm your identity',
  email: 'Share your email address',
  profile: 'Share your basic profile',
};

const OAuthConsent: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const authorizationId = params.get('authorization_id') ?? '';

  const [details, setDetails] = useState<AuthorizationDetails | null>(null);
  const [account, setAccount] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!authorizationId) {
        setError('This authorization link is missing an authorization_id.');
        setLoading(false);
        return;
      }
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        const next = window.location.pathname + window.location.search;
        navigate(`/auth?next=${encodeURIComponent(next)}`, { replace: true });
        return;
      }
      setAccount(sessionData.session.user.email ?? null);

      const { data, error: detailsError } = await oauth.getAuthorizationDetails(authorizationId);
      if (cancelled) return;
      if (detailsError) {
        setError(detailsError.message);
        setLoading(false);
        return;
      }
      const immediate = data?.redirect_url ?? data?.redirect_to;
      if (immediate && !data?.client) {
        window.location.href = immediate;
        return;
      }
      setDetails(data);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [authorizationId, navigate]);

  const decide = async (approve: boolean) => {
    setBusy(true);
    setError(null);
    const { data, error: decideError } = approve
      ? await oauth.approveAuthorization(authorizationId)
      : await oauth.denyAuthorization(authorizationId);
    if (decideError) {
      setBusy(false);
      setError(decideError.message);
      return;
    }
    const target = data?.redirect_url ?? data?.redirect_to;
    if (!target) {
      setBusy(false);
      setError('No redirect returned by the authorization server.');
      return;
    }
    window.location.href = target;
  };

  const clientName = details?.client?.name ?? details?.client?.client_name ?? 'this app';
  const scopes = (details?.scope ?? '').split(/\s+/).filter(Boolean);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>
            {loading ? 'Loading authorization request…' : `Connect ${clientName} to Future Level`}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          {!loading && !error && (
            <>
              <p className="text-sm text-muted-foreground">
                This lets {clientName} use this app as you.
              </p>
              {account && (
                <p className="text-sm">
                  Signed in as <span className="font-medium">{account}</span>
                </p>
              )}
              {details?.client?.redirect_uri && (
                <p className="text-xs text-muted-foreground break-all">
                  Redirects to {details.client.redirect_uri}
                </p>
              )}
              {scopes.length > 0 && (
                <ul className="space-y-1 text-sm">
                  {scopes.map((scope) => (
                    <li key={scope}>• {SCOPE_LABELS[scope] ?? `Additional permission requested: ${scope}`}</li>
                  ))}
                </ul>
              )}
              <p className="text-sm">
                {clientName} will be able to call this app&apos;s enabled tools while you are signed in.
              </p>
              <p className="text-xs text-muted-foreground">
                This does not bypass this app&apos;s permissions or backend policies.
              </p>
              <div className="flex gap-3">
                <Button className="flex-1" disabled={busy} onClick={() => decide(true)}>
                  Approve
                </Button>
                <Button variant="outline" className="flex-1" disabled={busy} onClick={() => decide(false)}>
                  Cancel connection
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OAuthConsent;
